import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Power, ExternalLink, Link2, Loader2, Save, Copy } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

/**
 * Coming-soon / maintenance toggle + tester preview link controls.
 * Lives in /admin/theme — moved out of the main Admin landing page.
 */
const MaintenanceCard = ({ setError }) => {
  const [enabled, setEnabled] = useState(null);
  const [saving, setSaving] = useState(false);
  const [code, setCode] = useState("");
  const [codeDraft, setCodeDraft] = useState("");
  const [codeSaving, setCodeSaving] = useState(false);
  const [codeSaved, setCodeSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/settings/maintenance");
      setEnabled(!!r.data?.enabled);
    } catch {
      setEnabled(true);
    }
    try {
      const r2 = await api.get("/admin/settings/maintenance-code");
      setCode(r2.data?.preview_access_code || "");
      setCodeDraft(r2.data?.preview_access_code || "");
    } catch { /* non-fatal */ }
  };

  useEffect(() => { load(); }, []);

  const toggle = async () => {
    if (enabled === null) return;
    const next = !enabled;
    if (next === false) {
      const ok = window.confirm(
        "Take the site live? Public visitors will see all pages instead of the Coming Soon splash."
      );
      if (!ok) return;
    }
    setSaving(true);
    setError?.("");
    try {
      const r = await api.put("/settings/maintenance", { enabled: next });
      setEnabled(!!r.data?.enabled);
    } catch (err) {
      setError?.(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveCode = async () => {
    setCodeSaving(true);
    setError?.("");
    try {
      const r = await api.put("/settings/maintenance", {
        preview_access_code: codeDraft.trim(),
      });
      const next = r.data?.preview_access_code || "";
      setCode(next);
      setCodeDraft(next);
      setCodeSaved(true);
      setTimeout(() => setCodeSaved(false), 1800);
    } catch (err) {
      setError?.(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCodeSaving(false);
    }
  };

  const shareUrl = code
    ? `${window.location.origin}/?preview=${encodeURIComponent(code)}`
    : "";

  const copyShareUrl = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const liveBg = enabled
    ? "bg-[#0A0F17] text-white"
    : "bg-[#6CABDD] text-[var(--brand-ink)]";

  return (
    <section
      data-testid="admin-maintenance-card"
      className={`mb-10 overflow-hidden rounded-md border border-black/10 ${liveBg}`}
    >
      <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Power size={14} strokeWidth={2.5} />
            <span className="label-kicker">Maintenance mode</span>
          </div>
          <h2 className="mt-2 font-display text-xl font-black tracking-tight sm:text-2xl">
            {enabled === null
              ? "Loading…"
              : enabled
              ? "Site is in coming-soon mode"
              : "Site is live to the public"}
          </h2>
          <p className="mt-1 max-w-xl text-sm opacity-80">
            {enabled
              ? "Public visitors see the splash page. You (admin) bypass it."
              : "Visitors see the full LAST hub. Flip this back on if you need to take the site offline again."}
          </p>
          {enabled ? (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Link
                to="/admin/page-content"
                data-testid="admin-edit-splash"
                className="inline-flex items-center gap-1 label-kicker underline underline-offset-4 opacity-80 hover:opacity-100"
              >
                Edit the splash copy <ExternalLink size={12} />
              </Link>
              <span className="opacity-40">·</span>
              <a
                href="/?as=visitor"
                target="_blank"
                rel="noreferrer"
                data-testid="admin-preview-as-visitor"
                className="inline-flex items-center gap-1 label-kicker underline underline-offset-4 opacity-80 hover:opacity-100"
              >
                Preview as visitor <ExternalLink size={12} />
              </a>
            </div>
          ) : (
            <a
              href="/?as=visitor"
              target="_blank"
              rel="noreferrer"
              data-testid="admin-preview-as-visitor"
              className="mt-3 inline-flex items-center gap-1 label-kicker underline underline-offset-4 opacity-80 hover:opacity-100"
            >
              Preview as visitor <ExternalLink size={12} />
            </a>
          )}
        </div>

        <button
          type="button"
          onClick={toggle}
          disabled={saving || enabled === null}
          data-testid="admin-maintenance-toggle"
          className={`inline-flex items-center justify-center gap-2 rounded-md px-5 py-3 font-display text-sm font-black uppercase tracking-wider transition-colors disabled:opacity-60 ${
            enabled
              ? "bg-[#6CABDD] text-[var(--brand-ink)] hover:bg-[#5A9AC9]"
              : "bg-[#0A0F17] text-white hover:bg-black"
          }`}
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Power size={14} strokeWidth={2.5} />}
          {enabled === null ? "Loading" : saving ? "Updating…" : enabled ? "Take site live" : "Bring back splash"}
        </button>
      </div>

      {enabled ? (
        <div className="border-t border-white/10 px-6 py-5">
          <div className="flex items-center gap-2">
            <Link2 size={14} strokeWidth={2.5} />
            <span className="label-kicker">Tester preview link</span>
          </div>
          <p className="mt-2 max-w-xl text-sm opacity-80">
            Set a passphrase and share the link below. Anyone opening it
            bypasses the splash for 7 days. Leave the field blank to disable.
          </p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="text"
              value={codeDraft}
              onChange={(e) => setCodeDraft(e.target.value)}
              placeholder="e.g. skyblues26"
              data-testid="admin-preview-code-input"
              className="w-full max-w-sm rounded-md border border-white/20 bg-black/30 px-3 py-2 font-mono text-sm text-white placeholder-white/40 focus:border-[#6CABDD] focus:outline-none"
            />
            <button
              type="button"
              onClick={saveCode}
              disabled={codeSaving || codeDraft === code}
              data-testid="admin-preview-code-save"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-[#6CABDD] px-4 py-2 font-display text-sm font-black uppercase tracking-wider text-[var(--brand-ink)] transition-colors hover:bg-[#5A9AC9] disabled:opacity-60"
            >
              {codeSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {codeSaving ? "Saving…" : codeSaved ? "Saved ✓" : "Save code"}
            </button>
          </div>

          {code ? (
            <div className="mt-4 rounded-md border border-white/15 bg-black/20 p-3">
              <div className="label-kicker opacity-70">Share this link</div>
              <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center">
                <code
                  data-testid="admin-preview-share-url"
                  className="block flex-1 overflow-x-auto whitespace-nowrap rounded bg-black/30 px-2 py-1.5 font-mono text-xs text-white/90"
                >
                  {shareUrl}
                </code>
                <button
                  type="button"
                  onClick={copyShareUrl}
                  data-testid="admin-preview-share-copy"
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-white/20 bg-white/5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-white/10"
                >
                  <Copy size={12} />
                  {copied ? "Copied ✓" : "Copy"}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export default MaintenanceCard;
